/**
 * WalkHereIndicator — the viewport pill for the right-click "Walk here" flow
 * (ENG-1050). One line per walkHereState phase, so the gesture is never silent.
 *
 * It exists because of the Doherty threshold: kimodo generation takes seconds,
 * far past the ~400ms where a user stops believing their click registered. The
 * pill is the DOM half of that feedback (WalkHereRoute is the 3D half).
 *
 * The error state is the one that PERSISTS and is dismissible — a failed
 * generation is the whole reason this flow has no fallback, so it must be
 * readable after the fact rather than fading. Everything else auto-clears.
 */
import { useEffect, useState } from 'react'
import { walkHereState } from '../lib/walkHereState'

const LABELS = {
  planning: 'Planning route',
  generating: 'Generating walk',
  walking: 'Walking',
  arrived: 'Arrived',
}

export default function WalkHereIndicator() {
  const [s, setS] = useState(() => walkHereState.getState())
  useEffect(() => walkHereState.subscribe(() => setS(walkHereState.getState())), [])

  if (s.phase === 'idle') return null
  const isError = s.phase === 'error'
  const busy = s.phase === 'planning' || s.phase === 'generating'

  return (
    <div
      className="aos-walkhere-pill"
      data-testid="walk-here-pill"
      data-phase={s.phase}
      data-busy={busy ? '1' : '0'}
      style={{ borderColor: isError ? 'var(--m-danger)' : 'var(--m-line)' }}
    >
      <style>{`
        .aos-walkhere-pill {
          position: absolute; top: 12px; left: 50%; transform: translateX(-50%);
          z-index: 30; display: flex; align-items: center; gap: 8px;
          max-width: min(420px, 70vw);
          padding: 5px 10px;
          background: var(--m-overlay); border: 1px solid var(--m-line);
          border-radius: var(--m-radius-sm);
          font-family: var(--m-font); font-size: 11px; color: var(--m-fg-2);
          animation: aos-walkhere-in 140ms var(--m-ease) both;
        }
        @keyframes aos-walkhere-in {
          from { opacity: 0; transform: translate(-50%, -6px) }
          to   { opacity: 1; transform: translate(-50%, 0) }
        }
        .aos-walkhere-dot {
          width: 6px; height: 6px; border-radius: 50%; flex: none;
          background: var(--m-accent);
        }
        .aos-walkhere-pill[data-busy="1"] .aos-walkhere-dot {
          animation: aos-walkhere-breathe 1.1s var(--m-ease) infinite;
        }
        @keyframes aos-walkhere-breathe {
          0%, 100% { opacity: .35; transform: scale(.8) }
          50%      { opacity: 1;   transform: scale(1.15) }
        }
        .aos-walkhere-pill[data-phase="error"] .aos-walkhere-dot { background: var(--m-danger) }
        .aos-walkhere-pill[data-phase="arrived"] .aos-walkhere-dot { background: var(--m-online) }
        .aos-walkhere-msg { font-family: var(--m-mono); color: var(--m-fg-3) }
        .aos-walkhere-pill[data-phase="error"] .aos-walkhere-msg { color: var(--m-danger) }
        .aos-walkhere-x {
          border: 0; background: transparent; cursor: pointer; padding: 0 2px;
          color: var(--m-fg-3); font: inherit; line-height: 1;
        }
        .aos-walkhere-x:hover { color: var(--m-fg) }
        @media (prefers-reduced-motion: reduce) {
          .aos-walkhere-pill, .aos-walkhere-pill .aos-walkhere-dot { animation: none }
        }
      `}</style>
      <span className="aos-walkhere-dot" />
      <span>{isError ? 'Walk here failed' : LABELS[s.phase]}</span>
      {s.message && <span className="aos-walkhere-msg">{s.message}</span>}
      {s.clamped && !isError && (
        <span className="aos-walkhere-msg">nearest reachable</span>
      )}
      {isError && (
        <button
          type="button"
          className="aos-walkhere-x"
          aria-label="Dismiss"
          onClick={() => walkHereState.reset()}
        >
          x
        </button>
      )}
    </div>
  )
}
